export const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const getAuthToken = () => localStorage.getItem('authToken');

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const api = {
  async signup(email, password, displayName, role = 'user', publicUsername = null, extraFields = {}) {
    const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, displayName, role, publicUsername, ...extraFields })
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.error || 'Signup failed');
      error.code = data.code;
      throw error;
    }
    localStorage.setItem('authToken', data.token);
    return data;
  },

  async login(email, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.error || 'Login failed');
      error.code = data.code;
      throw error;
    }
    localStorage.setItem('authToken', data.token);
    return data;
  },

  async getCurrentUser() {
    const token = getAuthToken();
    if (!token) return null;
    
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      localStorage.removeItem('authToken');
      return null;
    }
    return response.json();
  },

  logout() {
    localStorage.removeItem('authToken');
  },

  async getWasteReports() {
    const response = await fetch(`${API_BASE_URL}/api/waste-reports`);
    if (!response.ok) throw new Error('Failed to fetch reports');
    return response.json();
  },

  async getWasteReport(id) {
    const response = await fetch(`${API_BASE_URL}/api/waste-reports/${id}`);
    if (!response.ok) throw new Error('Failed to fetch report');
    return response.json();
  },

  async getUserReports(userId) {
    const response = await fetch(`${API_BASE_URL}/api/waste-reports/user/${userId}`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch user reports');
    return response.json();
  },

  async createWasteReport(reportData, file) {
    const formData = new FormData();
    Object.keys(reportData).forEach(key => {
      if (key === 'location') {
        formData.append(key, JSON.stringify(reportData[key]));
      } else {
        formData.append(key, reportData[key]);
      }
    });
    if (file) {
      formData.append('file', file);
    }

    const response = await fetch(`${API_BASE_URL}/api/waste-reports`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData
    });
    if (!response.ok) throw new Error('Failed to create report');
    return response.json();
  },

  async addComment(reportId, comment) {
    const response = await fetch(`${API_BASE_URL}/api/waste-reports/${reportId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders()
      },
      body: JSON.stringify(comment)
    });
    if (!response.ok) throw new Error('Failed to add comment');
    return response.json();
  },

  async submitCleanup(reportId, cleanupData, file) {
    const formData = new FormData();
    Object.keys(cleanupData).forEach(key => {
      if (typeof cleanupData[key] === 'object') {
        formData.append(key, JSON.stringify(cleanupData[key]));
      } else {
        formData.append(key, cleanupData[key]);
      }
    });
    if (file) {
      formData.append('file', file);
    }

    const response = await fetch(`${API_BASE_URL}/api/waste-reports/${reportId}/cleanup`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData
    });
    if (!response.ok) throw new Error('Failed to submit cleanup');
    return response.json();
  },

  async getDashboardStats(months = 6, userId = null) {
    const params = new URLSearchParams({ months: months.toString() });
    if (userId) params.append('userId', userId);
    
    const response = await fetch(`${API_BASE_URL}/api/dashboard/stats?${params}`);
    if (!response.ok) throw new Error('Failed to fetch dashboard stats');
    return response.json();
  },

  async getCorporateDashboardStats(months = 3) {
    const response = await fetch(`${API_BASE_URL}/api/corporate-dashboard/stats?months=${months}`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      if (response.status === 403) throw new Error('Subscriber access required');
      throw new Error('Failed to fetch corporate dashboard stats');
    }
    return response.json();
  },

  async getAuthorityBoundary(authorityName) {
    const response = await fetch(`${API_BASE_URL}/api/authority-boundary/${encodeURIComponent(authorityName)}`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch authority boundary');
    }
    return response.json();
  },

  async getAuthorityReports(authorityName) {
    const response = await fetch(`${API_BASE_URL}/api/authority-reports/${encodeURIComponent(authorityName)}`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch authority reports');
    return response.json();
  },

  async getNotifications() {
    const response = await fetch(`${API_BASE_URL}/api/corporate-dashboard/notifications`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  },

  async markNotificationsRead(notificationIds = null) {
    const response = await fetch(`${API_BASE_URL}/api/corporate-dashboard/notifications/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ notificationIds })
    });
    if (!response.ok) throw new Error('Failed to mark notifications as read');
    return response.json();
  },

  async updateProfile(profileData) {
    const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(profileData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to update profile');
    return data;
  },

  async changePassword(currentPassword, newPassword) {
    const response = await fetch(`${API_BASE_URL}/api/user/change-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to change password');
    return data;
  },

  async getProfile() {
    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch profile');
    return response.json();
  },

  async updateProfileData(profileData) {
    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(profileData)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to update profile');
    return data;
  },

  async getTeamMembers() {
    const response = await fetch(`${API_BASE_URL}/api/team/members`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch team members');
    return response.json();
  },

  async addTeamMember(email) {
    const response = await fetch(`${API_BASE_URL}/api/team/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ email })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to add team member');
    return data;
  },

  async removeTeamMember(id) {
    const response = await fetch(`${API_BASE_URL}/api/team/members/${id}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to remove team member');
    return data;
  },

  async uploadProfilePhoto(file) {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await fetch(`${API_BASE_URL}/api/auth/profile-photo`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to upload photo');
    return data;
  },

  // ================== OPERATIONAL FEATURES API ==================

  // AUTHORITY: Crew Management
  async getCrews() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/crews`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch crews');
    return response.json();
  },

  async createCrew(name, members = [], vehicleInfo = '') {
    const response = await fetch(`${API_BASE_URL}/api/corporate/crews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name, members, vehicleInfo })
    });
    if (!response.ok) throw new Error('Failed to create crew');
    return response.json();
  },

  async deleteCrew(crewId) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/crews/${crewId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete crew');
    return response.json();
  },

  // AUTHORITY: Assignments
  async getAssignments() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/assignments`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch assignments');
    return response.json();
  },

  async createAssignment(reportId, crewId, scheduledDate, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/corporate/assignments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reportId, crewId, scheduledDate, notes })
    });
    if (!response.ok) throw new Error('Failed to create assignment');
    return response.json();
  },

  async updateAssignmentStatus(assignmentId, status) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/assignments/${assignmentId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update assignment');
    return response.json();
  },

  async addComplianceFlag(assignmentId, flag, description) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/assignments/${assignmentId}/compliance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ flag, description })
    });
    if (!response.ok) throw new Error('Failed to add compliance flag');
    return response.json();
  },

  // PRIVATE COMPANY: Jobs
  async getJobs(status = null) {
    const params = status ? `?status=${status}` : '';
    const response = await fetch(`${API_BASE_URL}/api/corporate/jobs${params}`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch jobs');
    return response.json();
  },

  async respondToJob(jobId, response, notes = '') {
    const res = await fetch(`${API_BASE_URL}/api/corporate/jobs/${jobId}/respond`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ response, notes })
    });
    if (!res.ok) throw new Error('Failed to respond to job');
    return res.json();
  },

  async scheduleJob(jobId, scheduledDate, crewName, estimatedCost) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/jobs/${jobId}/schedule`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ scheduledDate, crewName, estimatedCost })
    });
    if (!response.ok) throw new Error('Failed to schedule job');
    return response.json();
  },

  async completeJob(jobId, actualCost) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/jobs/${jobId}/complete`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ actualCost })
    });
    if (!response.ok) throw new Error('Failed to complete job');
    return response.json();
  },

  async issueJob(reportId, assignedTo, estimatedCost) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/jobs/issue`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reportId, assignedTo, estimatedCost })
    });
    if (!response.ok) throw new Error('Failed to issue job');
    return response.json();
  },

  // PRIVATE COMPANY: Invoices
  async getInvoices() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/invoices`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch invoices');
    return response.json();
  },

  async createInvoice(jobId, issuedTo, items, amount, tax = 0) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/invoices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ jobId, issuedTo, items, amount, tax })
    });
    if (!response.ok) throw new Error('Failed to create invoice');
    return response.json();
  },

  // PRIVATE COMPANY: Performance
  async getPerformanceMetrics(months = 3) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/performance?months=${months}`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch performance metrics');
    return response.json();
  },

  // RECYCLING: Filtered Reports
  async getRecyclableReports() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/recyclable-reports`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch recyclable reports');
    return response.json();
  },

  // RECYCLING: Material Rates & Estimator
  async getMaterialRates() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/material-rates`);
    if (!response.ok) throw new Error('Failed to fetch material rates');
    return response.json();
  },

  async estimateMaterialValue(materials) {
    const response = await fetch(`${API_BASE_URL}/api/corporate/estimate-value`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ materials })
    });
    if (!response.ok) throw new Error('Failed to estimate value');
    return response.json();
  },

  // RECYCLING: Inventory
  async getInventory() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/inventory`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch inventory');
    return response.json();
  },

  async addInventoryItem(reportId, materialType, category, quantity, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/corporate/inventory`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ reportId, materialType, category, quantity, notes })
    });
    if (!response.ok) throw new Error('Failed to add inventory item');
    return response.json();
  },

  // Get available companies (for job assignment)
  async getCompanies() {
    const response = await fetch(`${API_BASE_URL}/api/corporate/companies`);
    if (!response.ok) throw new Error('Failed to fetch companies');
    return response.json();
  },

  async claimFoodDonation(reportId) {
    const response = await fetch(`${API_BASE_URL}/api/waste-reports/${reportId}/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() }
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to claim food donation');
    return data;
  },

  async reportMissedBin(data) {
    const response = await fetch(`${API_BASE_URL}/api/missed-bin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to report missed bin');
    return result;
  },

  async getMissedBinReports() {
    const response = await fetch(`${API_BASE_URL}/api/missed-bin/authority`, {
      headers: authHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch missed bin reports');
    return response.json();
  }
};

export default api;
